import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getRecentArticles(4).filter(a => a.id !== article?.id).slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <i className="ri-error-warning-line text-5xl text-gray-600 mb-4"></i>
          <h1 className="text-xl font-bold text-white">文章未找到</h1>
          <Link to="/" className="mt-4 text-amber-400 hover:text-amber-300 transition-colors">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '69视频',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50 text-foreground-50">
        <Navbar />

        <main>
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.categoryId === 'cat-1' ? 'sigua-video' : article.categoryId === 'cat-2' ? 'blueberry-video' : article.categoryId === 'cat-3' ? 'tangxin-video' : article.categoryId === 'cat-4' ? 'heitao-video' : article.categoryId === 'cat-5' ? 'yaojing-video' : 'taohua-video'}` },
            { label: article.title },
          ]} />

          {/* Article Hero */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-6">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden aspect-[21/9]">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Article Content */}
          <article className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categoryId === 'cat-1' ? 'sigua-video' : article.categoryId === 'cat-2' ? 'blueberry-video' : article.categoryId === 'cat-3' ? 'tangxin-video' : article.categoryId === 'cat-4' ? 'heitao-video' : article.categoryId === 'cat-5' ? 'yaojing-video' : 'taohua-video'}`}
                  className="px-3 py-1 text-xs rounded-lg bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-gray-600">{article.readTime} 分钟阅读</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">{article.title}</h1>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center">
                    <i className="ri-user-line text-amber-400 text-sm"></i>
                  </div>
                  <span>{article.author}</span>
                </div>
                <span className="text-gray-700">|</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                <span className="text-gray-700">|</span>
                <span><i className="ri-eye-line mr-1"></i>{article.viewCount.toLocaleString()} 阅读</span>
                <span className="text-gray-700">|</span>
                <span><i className="ri-heart-line mr-1"></i>{article.likeCount.toLocaleString()} 点赞</span>
              </div>

              <div className="mt-8 prose prose-invert max-w-none">
                <div
                  className="text-gray-300 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{
                    __html: article.content
                      .replace(/<h2>/g, '<h2 class="text-xl md:text-2xl font-bold text-white mt-10 mb-4">')
                      .replace(/<p>/g, '<p class="text-sm md:text-base text-gray-300 leading-relaxed mb-4">'),
                  }}
                />
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map((tag, i) => (
                  <span key={i} className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-gray-400">{tag}</span>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <Link to="/" className="flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors">
                    <i className="ri-arrow-left-line"></i> 返回首页
                  </Link>
                  <div className="flex items-center gap-3">
                    <button className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                      <i className="ri-share-forward-line"></i>
                    </button>
                    <button className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 hover:text-red-400 transition-colors cursor-pointer">
                      <i className="ri-heart-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 lg:px-8 py-12 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-8">
                  相关<span className="text-amber-400">推荐</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-all cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-white text-sm group-hover:text-amber-400 transition-colors line-clamp-2">{ra.title}</h3>
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{ra.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}